import {
  CrossPlatformAbbreviations,
  IAdminConfiguration,
  ServiceTypes,
  convertToDecimals,
  currency,
  fallbackText,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { goToFallback } from '@pharmaplan/common/helpers/utils';
import {
  Constants,
  telephoneInputMask,
  TextFieldVariants,
} from '../../../../helpers/Constants';
import strings from '../../../../localization';
import { IInputModalConfig } from '../../../Modals/InputModal/InputModal';
import styles from './styles';
import genericClasses from '../../../../theme/GenericClasses';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
const title = isSelfService ? strings.pharmagap : strings.pharmaPlan;

const keysToRemove = {
  pharmacyRate: 'pharmacyRate',
  pharmacistRate: 'pharmacistRate',
  techRate: 'techRate',
  salesForceKey: 'salesForceKey',
  ratePharmacy: 'ratePharmacy',
  studentRate: 'studentRate',
};

export const toProfessionalPharmacy = (
  professional: number | null | undefined,
  pharmacy: number | null | undefined,
) =>
  `${currency}${convertToDecimals(professional) || '-'} / $${convertToDecimals(pharmacy) || '-'}`;

const {
  pharmacistRate: pharmacistRateKey,
  pharmacyRate: pharmacyRateKey,
  techRate: techRateKey,
  studentRate: studentRateKey,
  salesForceKey,
  ratePharmacy: ratePharmacyKey,
} = keysToRemove;

export const handleChip = (value: boolean) =>
  ({
    chipStyle: value ? styles.bgPositive : styles.bgNegative,
    value: value ? strings.yes : strings.no,
  });

export const settingsInfoConfig = (data: IAdminConfiguration) => {
  const {
    ratePharmacistDays,
    ratePharmacyDays,
    minHourWorkshift,
    pharmacistRate,
    pharmacyRate,
    representativeName,
    representativeEmail,
    representativePhone,
    representativePhoneExt,
    salesForceActive,
    salesForcePassword,
    salesForceSecurityToken,
    salesForceURL,
    salesForceUserName,
    envoiceBCCAdmin,
    technicianRate,
    showBookingDetails,
    bookingStateExpiration,
    pharmacyReminderBefore,
    enableChat,
    basicPackagePrice,
    unlimitedPackagePrice,
    tpsPercentage,
    tvqPercentage,
    taxOnTax,
    tpsNumber,
    tvqNumber,
    companyName,
    address,
    phoneNumber,
    city,
    province,
    pharmacyWeekDayHourlyRate = 0,
    pharmacyWeekendHourlyRate = 0,
    studentWeekDayHourlyRate = 0,
    studentWeekendHourlyRate = 0,
    technicianWeekDayHourlyRate = 0,
    technicianWeekendHourlyRate = 0,
    pharmacistWeekDayHourlyRate = 0,
    pharmacistWeekendHourlyRate = 0,
    bookingInvitationConfiguration,
    sendBookingInvitationReminder,
    studentRate,
  } = data ?? {};

  const {
    professionalCapacity,
    softwareKnowledge,
    preferredShiftHours,
    remoteRegions,
    maxTravelDistance,
    includePPShifts,
    availability,
    repeatAfterDays,
  } = bookingInvitationConfiguration ?? {};

  const hourlyRates = [
    {
      key: pharmacyRateKey,
      xs: 4,
      label: strings.formatString(
        strings.defaultHourlyRate,
        strings.pharmacy,
      ) as string,
      value: `$${convertToDecimals(pharmacyRate)}`,
    },
    {
      key: 'weekDayPharmacyRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekdayRate,
        strings.pharmacy,
      ) as string,
      value: `$${convertToDecimals(pharmacyWeekDayHourlyRate)}`,
    },
    {
      key: 'weekendPharmacyRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekendRate,
        strings.pharmacy,
      ) as string,
      value: `$${convertToDecimals(pharmacyWeekendHourlyRate)}`,
    },
  ];

  const commonContent = [
    {
      key: 0,
      label: strings.noOfDaysToRatePharmacist,
      value: ratePharmacistDays,
    },
    isSelfService && {
      key: ratePharmacyKey,
      label: strings.noOfDaysToRatePharmacy,
      value: ratePharmacyDays ?? '',
    },
    {
      key: 1,
      label: strings.minHoursWorkshift,
      value: minHourWorkshift,
    },
  ].filter(Boolean) as Array<{ key: string; label: string; value: any }>;

  let rates = [
    {
      key: pharmacistRateKey,
      xs: 4,
      label: strings.formatString(
        strings.defaultHourlyRate,
        strings.pharmacist,
      ) as string,
      value: `$${convertToDecimals(pharmacistRate)}`,
    },
    {
      key: 'pharmacistWeekdayRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekdayRate,
        strings.pharmacist,
      ) as string,
      value: `$${convertToDecimals(pharmacistWeekDayHourlyRate)}`,
    },
    {
      key: 'pharmacistWeekendRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekendRate,
        strings.pharmacist,
      ) as string,
      value: `$${convertToDecimals(pharmacistWeekendHourlyRate)}`,
    },
    {
      key: techRateKey,
      xs: 4,
      label: strings.formatString(
        strings.defaultHourlyRate,
        `${strings.tech}.`,
      ) as string,
      value: `$${convertToDecimals(technicianRate)}`,
    },
    {
      key: 'technicianWeekdayRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekdayRate,
        strings.technician,
      ) as string,
      value: `$${convertToDecimals(technicianWeekDayHourlyRate)}`,
    },
    {
      key: 'technicianWeekendRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekendRate,
        strings.technician,
      ) as string,
      value: `$${convertToDecimals(technicianWeekendHourlyRate)}`,
    },
    {
      key: studentRateKey,
      xs: 4,
      label: strings.formatString(
        strings.defaultHourlyRate,
        `${strings.student}`,
      ) as string,
      value: `$${convertToDecimals(studentRate)}`,
    },
    {
      key: 'studentWeekdayRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekdayRate,
        strings.student,
      ) as string,
      value: `$${convertToDecimals(studentWeekDayHourlyRate)}`,
    },
    {
      key: 'studentWeekendRate',
      xs: 4,
      label: strings.formatString(
        strings.userWeekendRate,
        strings.student,
      ) as string,
      value: `$${convertToDecimals(studentWeekendHourlyRate)}`,
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    value: any;
    xs?: number;
  }>;

  if (!isSelfService) {
    rates.unshift(...hourlyRates);
    rates = rates.filter((item) =>
      item.key !== ratePharmacyKey);
  }

  const selfPreContent = [
    {
      key: '-1',
      label: strings.formatString(
        strings.feesFormat,
        strings.unlimited,
      ) as string,
      value: `$${convertToDecimals(unlimitedPackagePrice)}`,
    },
    {
      key: '-2',
      label: strings.formatString(
        strings.feesFormat,
        strings.standard,
      ) as string,
      value: `$${convertToDecimals(basicPackagePrice)}`,
    },
  ];

  const selfPostContent = [
    {
      key: '6',
      label: strings.requestPendingPeriod,
      value: `${bookingStateExpiration}${strings.hoursAbbreviation}`,
    },
    {
      key: '11',
      label: strings.pharmacyReminder,
      value: `${pharmacyReminderBefore}${strings.hoursAbbreviation}`,
    },
    {
      key: '12',
      label: strings.tps,
      value: `${tpsPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '13',
      label: strings.tvq,
      value: `${tvqPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '14',
      label: strings.taxOnTax,
      displayChip: true,
      ...handleChip(!!taxOnTax),
    },
  ];

  if (isSelfService) {
    commonContent.unshift(...selfPreContent);
    commonContent.push(...selfPostContent);
  }

  const pharmagapAccountSettings = {
    key: -1,
    title: strings.accountSettings,
    content: [
      {
        key: 0,
        label: strings.companyName,
        value: goToFallback(companyName, fallbackText),
      },
      {
        key: 1,
        label: strings.companyProvince,
        value: goToFallback(province, fallbackText),
      },
      {
        key: 2,
        label: strings.companyCity,
        value: goToFallback(city, fallbackText),
      },
      {
        key: 3,
        label: strings.companyAddress,
        value: goToFallback(address, fallbackText),
      },
      {
        key: 4,
        label: strings.companyPhone,
        value: goToFallback(phoneNumber, fallbackText),
      },
      {
        key: 5,
        label: strings.businessTPS,
        value: goToFallback(tpsNumber, fallbackText),
      },
      {
        key: 6,
        label: strings.businessTVQ,
        value: goToFallback(tvqNumber, fallbackText),
      },
    ].filter(Boolean) as Array<{
      key: number;
      label: string;
      value: string | number;
    }>,
  };

  let arr = [
    pharmagapAccountSettings,
    {
      key: 0,
      title: strings.formatString(strings.customSettings, title) as string,
      content: commonContent,
    },
    {
      key: 0.5,
      title: strings.hourlyRateSettings,
      content: rates,
    },
    {
      key: 1,
      title: strings.formatString(
        strings.customSettings,
        strings.customerServiceRepresentative,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.representativeName,
          value: representativeName,
        },
        {
          key: 1,
          label: strings.representativeEmail,
          value: representativeEmail,
        },
        {
          key: 2,
          label: strings.representativePhone,
          value: representativePhone,
        },
        {
          key: 3,
          label: strings.representativeExtPhone,
          value: representativePhoneExt,
        },
      ],
    },
    {
      key: salesForceKey,
      title: strings.formatString(
        strings.customSettings,
        strings.salesforce,
      ) as string,
      content: [
        {
          key: 3,
          label: strings.urlOfSalesForce,
          value: salesForceURL,
        },
        {
          key: 4,
          label: strings.integrationSalesForce,
          displayChip: true,
          ...handleChip(salesForceActive),
        },
      ],
    },
    {
      key: 1.5,
      title: strings.bookingInvitationEmailsSettings,
      content: [
        {
          key: 1,
          label: strings.professionalCapacity,
          displayChip: true,
          ...handleChip(professionalCapacity),
        },
        {
          key: 2,
          label: strings.softwareKnowledge,
          displayChip: true,
          ...handleChip(softwareKnowledge),
        },
        {
          key: 3,
          label: strings.preferredShiftDuration,
          displayChip: true,
          ...handleChip(preferredShiftHours),
        },
        {
          key: 4,
          label: strings.remoteRegions,
          displayChip: true,
          ...handleChip(remoteRegions),
        },
        {
          key: 4.5,
          label: strings.availability,
          displayChip: true,
          ...handleChip(availability),
        },
        {
          key: 5,
          label: strings.maxTravelDistance,
          displayChip: true,
          ...handleChip(maxTravelDistance),
        },
        isSelfService && {
          key: 7,
          label: strings.formatString(
            strings.showCrossShiftFormat,
            CrossPlatformAbbreviations.full,
            CrossPlatformAbbreviations.self,
          ),
          displayChip: true,
          ...handleChip(includePPShifts),
        },
        {
          key: 0,
          label: strings.sendBookingInvitationEmails,
          displayChip: true,
          ...handleChip(sendBookingInvitationReminder),
        },
        {
          key: 1.5,
          label: strings.bookingInvitationFrequencyDays,
          value: repeatAfterDays,
        },
      ].filter(Boolean) as Array<{
        key: number;
        label: string;
        value: string;
        displayChip: boolean;
      }>,
    },
    {
      key: 2,
      title: strings.formatString(
        strings.customSettings,
        strings.more,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.sendAllEmailsBCC,
          displayChip: true,
          ...handleChip(envoiceBCCAdmin),
        },
        {
          key: 1,
          label: strings.showAllBookingDetails,
          displayChip: true,
          ...handleChip(showBookingDetails),
        },
        {
          key: 2,
          label: strings.enableChat,
          displayChip: true,
          ...handleChip(enableChat),
        },
      ],
    },
  ];

  if (isSelfService) {
    arr = arr.filter((item) =>
      item.key !== salesForceKey);
  }

  return arr;
};

export const createNewForm: IInputModalConfig[] = [
  {
    key: '0',
    name: 'name',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.name,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '1',
    name: 'email',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.email,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '2',
    name: 'phone',
    gridLabel: strings.phone,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    fieldXs: 8,
    labelXs: 4,
    type: Constants.formInputType.masked,
    mask: telephoneInputMask,
    maxLength: 12,
  },
  {
    key: '3',
    name: 'received',
    type: Constants.formInputType.checkbox,
    gridLabel: strings.active,
    fieldXs: 8,
    labelXs: 4,
    customClass: genericClasses.checkboxContainer,
    customCheckboxStyle: genericClasses.checkboxStyle,
    gridLabelClass: styles.gridLabel,
  },
];
